/**
 * PuckBookingConfirmationMacroComponent
 *
 * Renders the Booking Confirmation macro using the Puck template system.
 * This component loads the template from the database and renders it with
 * actual reservation data.
 *
 * Template key: @MACROS/BOOKING-CONFIRMATION
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Render, type Data as PuckData } from '@measured/puck';
import { format, differenceInHours, startOfDay } from 'date-fns';

import {
    multiplyAndRemoveDecimal,
    ReservationState,
    getDisplayName,
    ReservationPosItem,
} from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/model';
import {
    getProgramTypeDisplayName,
    getRoomTypeDisplayName,
} from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/OrderDetailsComponent';

import { useModelState } from '../../../../js/useModelState';
import { countProgramDaysBetween, longToMoney, longToTHBWithPrefix } from '../../../../js/utils';
import { MacroCopyButtons } from './MacroCopyButtons';
import { programOperatingDays } from '../../../../js/programOperatingDays';
import { useConfirmationState } from './useConfirmationState';

// Import Puck infrastructure
import { getMacroPuckConfig } from 'phuketfit-components/dist/MacroPuck/MacroPuckConfig';
import { loadMacroPageData } from '../../MacroPuck/macroPuckClient';
import {
    MacroBindingProvider,
    createBindingValues,
    type MacroBindingValues,
} from 'phuketfit-components/dist/MacroPuck/MacroBindingContext';
import {
    MacroCollectionsProvider,
    type MacroCollectionsValue,
    type ProgramBookingItem,
    type RoomBookingItem,
    type PosItemData,
} from 'phuketfit-components/dist/MacroPuck/MacroCollectionContext';

// Template key for this macro
const TEMPLATE_KEY = '@MACROS/BOOKING-CONFIRMATION';

export interface PuckBookingConfirmationMacroComponentProps {
    reservationState: ReservationState;
}

export function PuckBookingConfirmationMacroComponent({
    reservationState,
}: PuckBookingConfirmationMacroComponentProps) {
    useConfirmationState(reservationState);
    // Ensure component re-renders if reservationState.Person changes
    useModelState(reservationState.Person);

    // State for loading and errors
    const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [pageData, setPageData] = useState<PuckData | null>(null);

    // State for computed values
    const [firstName, setFirstName] = useState<string>('');

    // Ref for copy functionality
    const previewRef = useRef<HTMLDivElement>(null);

    // Load template data on mount
    useEffect(() => {
        let isMounted = true;

        async function loadTemplate() {
            try {
                setStatus('loading');
                setErrorMessage(null);

                const data = await loadMacroPageData(TEMPLATE_KEY);

                if (isMounted) {
                    setPageData(data);
                }
            } catch (error: any) {
                if (isMounted) {
                    console.error('Failed to load Puck template:', error);
                    setErrorMessage(error?.message ?? 'Failed to load template');
                    setStatus('error');
                }
            }
        }

        loadTemplate();

        return () => {
            isMounted = false;
        };
    }, []);

    // Fetch display name
    useEffect(() => {
        const person = reservationState.Person.value;
        if (person) {
            getDisplayName(person, reservationState)
                .then(name => {
                    const nameParts = (name || '').split(' ');
                    setFirstName(nameParts[0] || 'Client');
                })
                .catch(error => {
                    console.error('Error fetching display name:', error);
                    setFirstName('Client');
                });
        } else {
            setFirstName('Client');
        }
    }, [reservationState.Person.value, reservationState]);

    // When both template and name are ready, set status to ready
    useEffect(() => {
        if (pageData && firstName) {
            setStatus('ready');
        }
    }, [pageData, firstName]);

    // Transform reservation data into binding values
    const bindingValues = ((): MacroBindingValues | null => {
        if (!firstName) return null;

        // Calculate totals using longs
        const payableLong = multiplyAndRemoveDecimal(reservationState.AmountPayableTotal.value);
        const dueLong = multiplyAndRemoveDecimal(reservationState.AmountDueTotal.value);
        const totalPaid = payableLong - dueLong;
        const remainderDue = dueLong;

        return createBindingValues({
            FirstName: firstName,
            DepositAmount: longToTHBWithPrefix(totalPaid),
            RemainderAmount: longToTHBWithPrefix(remainderDue),
            TotalPrice: longToTHBWithPrefix(payableLong),
        });
    })();

    // Transform reservation data into collections
    const collections = ((): MacroCollectionsValue => {
        // Transform programs
        const programs = reservationState.ProgramBookings.toArray();
        const transformedPrograms: Record<string, ProgramBookingItem & { priceRaw: number }> = {};

        for (const program of programs) {
            const programName = getProgramTypeDisplayName(reservationState, program.ProgramType.value);
            const programPrice = multiplyAndRemoveDecimal(program.Price.price.value.payableAmount);
            const programDays = countProgramDaysBetween(program.Start.value, program.End.value);
            const programStartDate = format(program.Start.value, 'MMMM d, yyyy');
            const programEndDate = format(program.End.value, 'MMMM d, yyyy');

            const groupKey = `${programName}_${programStartDate}_${programEndDate}`;
            const existing = transformedPrograms[groupKey];

            if (!existing) {
                transformedPrograms[groupKey] = {
                    Name: programName,
                    Days: programDays,
                    ClosureNote: programOperatingDays(program.Start.value, program.End.value).closureNote,
                    StartDate: programStartDate,
                    EndDate: programEndDate,
                    Price: `฿${longToMoney(programPrice)}`,
                    Quantity: 1,
                    priceRaw: programPrice,
                };
            } else {
                existing.priceRaw += programPrice;
                existing.Price = `฿${longToMoney(existing.priceRaw)}`;
                existing.Quantity += 1;
            }
        }

        // Transform rooms
        const rooms = reservationState.RoomBookings.toArray();
        const transformedRooms: Record<string, RoomBookingItem & { priceRaw: number }> = {};

        for (const room of rooms) {
            const roomName = getRoomTypeDisplayName(reservationState, room.RoomType.value);
            const roomPrice = multiplyAndRemoveDecimal(room.CalculatedPrice.value.totalPayableAmount);
            const roomNights = Math.ceil(differenceInHours(room.CheckOut.value, room.CheckIn.value) / 24) | 0;
            const roomCheckInDate = format(room.CheckIn.value, 'MMMM d, yyyy');
            const roomCheckOutDate = format(room.CheckOut.value, 'MMMM d, yyyy');

            const groupKey = `${roomName}_${roomCheckInDate}_${roomCheckOutDate}`;
            const existing = transformedRooms[groupKey];

            if (!existing) {
                transformedRooms[groupKey] = {
                    RoomType: roomName,
                    Nights: roomNights,
                    CheckInDate: roomCheckInDate,
                    CheckOutDate: roomCheckOutDate,
                    Price: `฿${longToMoney(roomPrice)}`,
                    Quantity: 1,
                    priceRaw: roomPrice,
                };
            } else {
                existing.priceRaw += roomPrice;
                existing.Price = `฿${longToMoney(existing.priceRaw)}`;
                existing.Quantity += 1;
            }
        }

        // Transform POS items
        const posItems = reservationState.PosItems.value;
        const transformedPosItems: PosItemData[] = posItems.map((item: ReservationPosItem) => ({
            Description: item.Description,
            Quantity: item.Quantity,
            Price: `฿${longToMoney(multiplyAndRemoveDecimal(item.Amount * item.Quantity))}`,
        }));

        return {
            programs: Object.values(transformedPrograms).map(({ priceRaw, ...rest }) => rest),
            rooms: Object.values(transformedRooms).map(({ priceRaw, ...rest }) => rest),
            posItems: transformedPosItems,
        };
    })();

    // Get the Puck config
    const config = useMemo(() => getMacroPuckConfig(), []);

    // Render loading state
    if (status === 'loading') {
        return <div className="p-4">Loading booking confirmation template...</div>;
    }

    // Render error state
    if (status === 'error') {
        return (
            <div className="p-4 text-red-600 border border-red-600 rounded">
                Error loading template: {errorMessage}
                <br />
                <small>Template key: {TEMPLATE_KEY}</small>
            </div>
        );
    }

    // Render ready state
    if (!pageData || !bindingValues) {
        return <div className="p-4">Preparing template data...</div>;
    }

    return (
        <div>
            <style>
                {`
                    .puck-macro-preview {
                        font-family: Arial, sans-serif;
                        font-size: 12px;
                        line-height: 22px;
                        color: #333;
                    }
                    .puck-macro-preview * { line-height: 22px; }
                    /* Zendesk CKEditor style: direct children get 8px margins, p has no margin */
                    .puck-macro-preview > * { margin-top: 8px; margin-bottom: 8px; }
                    .puck-macro-preview > *:first-child { margin-top: 12px; }
                    .puck-macro-preview p { line-height: 22px; margin: 0; }
                    /* Zendesk: trailing <br> creates double line break */
                    .puck-macro-preview p:has(> br:last-child) { padding-bottom: 22px; }
                    .puck-macro-preview ul {
                      padding-left: 15px;
                      list-style-type: disc;
                      margin-left: 8px;
                      margin-top: 0;
                    }
                    .puck-macro-preview li { line-height: 22px; }
                    .puck-macro-preview hr { margin: 15px 0 !important; border: none; border-top: 1px solid #ccc; }
                    .puck-macro-preview a { color: #1264a3; text-decoration: underline; }
                    .puck-macro-preview strong { font-weight: bold; }
                    .puck-macro-preview em { font-style: italic; }
                    .puck-macro-preview u { text-decoration: underline; }
                `}
            </style>
            <div style={{ marginBottom: '10px' }}>
                <MacroCopyButtons
                    getHtml={() => previewRef.current?.innerHTML ?? null}
                    getText={() => previewRef.current?.textContent ?? null}
                />
            </div>
            <div
                ref={previewRef}
                className="puck-macro-preview"
            >
                <MacroBindingProvider value={bindingValues}>
                    <MacroCollectionsProvider value={collections}>
                        <Render config={config} data={pageData} />
                    </MacroCollectionsProvider>
                </MacroBindingProvider>
            </div>
        </div>
    );
}
